import React, { useEffect, useRef, useState } from "react";

const ANALYTICS_CONSENT_KEY = "grantaide-analytics-consent";

const clearGoogleAnalyticsCookies = () => {
  const analyticsCookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  const rootDomain = window.location.hostname.replace(/^www\./, "");

  analyticsCookieNames.forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    if (rootDomain.includes(".")) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${rootDomain}; SameSite=Lax`;
    }
  });
};

function CookieBottomBar() {
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return !window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    } catch {
      return true;
    }
  });
  const dialogRef = useRef(null);
  const firstInteractiveRef = useRef(null);

  useEffect(() => {
    const openPrivacySettings = () => setIsOpen(true);

    window.addEventListener("grantaide:open-privacy-settings", openPrivacySettings);
    return () => window.removeEventListener("grantaide:open-privacy-settings", openPrivacySettings);
  }, []);

  useEffect(() => {
    if (navigator.globalPrivacyControl !== true) return;

    try {
      window.localStorage.setItem(ANALYTICS_CONSENT_KEY, "denied");
    } catch {
      // Consent still applies for this page when storage is unavailable.
    }
    window.grantAideSetAnalyticsConsent?.(false);
    clearGoogleAnalyticsCookies();
    setIsOpen(false);
  }, []);

  const handleRejectAnalytics = () => {
    try {
      window.localStorage.setItem(ANALYTICS_CONSENT_KEY, "denied");
    } catch {
      // Analytics remains disabled for this page.
    }
    window.grantAideSetAnalyticsConsent?.(false);
    clearGoogleAnalyticsCookies();
    setIsOpen(false);
  };

  const handleAllowAnalytics = () => {
    if (navigator.globalPrivacyControl === true) {
      handleRejectAnalytics();
      return;
    }

    try {
      window.localStorage.setItem(ANALYTICS_CONSENT_KEY, "granted");
    } catch {
      // Analytics can still be enabled for this page.
    }
    window.grantAideSetAnalyticsConsent?.(true);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocusedElement = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => firstInteractiveRef.current?.focus());

    const keepFocusInsideModal = (event) => {
      if (event.key !== "Tab" || !dialogRef.current) return;

      const interactiveElements = Array.from(dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (!interactiveElements.length) return;

      const firstElement = interactiveElements[0];
      const lastElement = interactiveElements[interactiveElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", keepFocusInsideModal);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", keepFocusInsideModal);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
      previouslyFocusedElement?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto overscroll-contain bg-black/70 py-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
      aria-describedby="cookie-modal-description"
    >
      <div className="relative mx-4 w-full max-w-[740px] overflow-hidden rounded-2xl border border-white/10 bg-[#0d011c] shadow-2xl">
        <div
          className="absolute left-0 right-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-[#81f8bf] via-[#3c88f1] to-[#cb1ccd]"
          aria-hidden="true"
        />

        <header className="grid min-h-[76px] grid-cols-[1fr_auto_1fr] items-center gap-4 px-6 py-4 sm:px-8">
          <div className="justify-self-start text-xl font-extrabold tracking-tight text-white sm:text-2xl">
            Grant<span className="bg-gradient-to-r from-[#81f8bf] via-[#3c88f1] to-[#cb1ccd] bg-clip-text text-transparent">AI</span>de
          </div>
          <h2 id="cookie-modal-title" className="text-center text-base font-bold text-white sm:text-xl">
            We Value Your Privacy
          </h2>
          <div aria-hidden="true" />
        </header>

        <section className="border-t border-[#3c88f1]/35 px-6 py-7 sm:px-8 sm:py-8">
          <h3 className="mb-3 text-base font-bold text-white">This website uses cookies</h3>
          <p id="cookie-modal-description" className="text-sm leading-7 text-[#c5c5c5] sm:text-base">
            We use cookies to gather information and documents from grantors’ websites and other resources to implement with the grant writing process. Read our{" "}
            <a ref={firstInteractiveRef} href="/privacypolicy" className="font-semibold text-white underline underline-offset-2 hover:text-[#cdbde8]">
              Privacy Policy
            </a>.
          </p>
        </section>

        <div className="flex flex-col gap-3 border-t border-[#3c88f1]/35 bg-white/[0.02] px-6 py-5 sm:flex-row sm:px-8">
          <button
            type="button"
            onClick={handleAllowAnalytics}
            className="w-full rounded-lg border border-[#8749e7] bg-[#8749e7] p-3 text-sm font-semibold text-white transition-colors hover:bg-[#7438d1]"
          >
            Allow
          </button>
          <button
            type="button"
            onClick={handleRejectAnalytics}
            className="w-full rounded-lg border border-[#8749e7]/70 bg-white/5 p-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={handleAllowAnalytics}
            className="w-full rounded-lg border border-[#ff00d9]/70 bg-white/5 p-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
          >
            Allow non essential cookies
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieBottomBar;
